import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/pages/PageHeader";
import { Card } from "../components/ui/Card";
import { Table, type TableColumn, type TableRow } from "../components/ui/Table";
import { Button } from "../components/ui/Button";
import {
  ImpactReportService,
  type ImpactReportLabTypeFilter,
  type ImpactReportLevel,
  type OrganisationImpactReport,
  type ImpactReportYearsResponse,
} from "../services/impactReportService";
import { formatNumber } from "../utils/format";

interface ImpactEstimateReportsProps {
  labTypeSubscriptions?: string[];
}

const labTableColumns: TableColumn[] = [
  { key: "lab_name", label: "Lab Name" },
  { key: "champion", label: "Champion" },
  { key: "state", label: "Report State" },
  { key: "cost_aud", label: "Cost ($AUD)" },
  { key: "energy_kwh", label: "Energy (kWh)" },
  { key: "emissions_kgco2e", label: "Emissions (kgCO2e)" },
  { key: "waste_kg", label: "Waste (kg)" },
  { key: "data_tb", label: "Data (TB)" },
  { key: "notes", label: "Notes" },
];

const totalsColumns: TableColumn[] = [
  { key: "cost_aud", label: "Cost ($AUD)" },
  { key: "energy_kwh", label: "Energy (kWh)" },
  { key: "emissions_kgco2e", label: "Emissions (kgCO2e)" },
  { key: "waste_kg", label: "Waste (kg)" },
  { key: "data_tb", label: "Data (TB)" },
];

/** Solvent volume is Green Chemistry only, so Silver wet labs only. */
const solventVolumeColumn: TableColumn = { key: "solvent_volume_l", label: "Solvent (L)" };

const withSolventVolume = (
  columns: TableColumn[],
  level: ImpactReportLevel,
  insertBefore: string,
): TableColumn[] => {
  if (level !== "silver") {
    return columns;
  }
  const index = columns.findIndex((column) => column.key === insertBefore);
  if (index === -1) {
    return [...columns, solventVolumeColumn];
  }
  return [...columns.slice(0, index), solventVolumeColumn, ...columns.slice(index)];
};

const formatNullableNumber = (value: number | null) =>
  typeof value === "number" ? formatNumber(value) : "-";

const toTitle = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const toLabRows = (report: OrganisationImpactReport): TableRow[] =>
  report.rows.map((row) => {
    const stateLabel = toTitle(row.report_state);
    const notes =
      row.missing_reasons.length > 0 ? row.missing_reasons.join("; ") : "-";

    return {
      id: row.lab_id,
      lab_name: row.lab_name,
      champion: row.champion ?? "Unassigned",
      state: stateLabel,
      cost_aud:
        row.report_state === "available" ? formatNumber(row.metrics.cost_aud) : "-",
      energy_kwh:
        row.report_state === "available" ? formatNumber(row.metrics.energy_kwh) : "-",
      emissions_kgco2e:
        row.report_state === "available"
          ? formatNumber(row.metrics.emissions_kgco2e)
          : "-",
      waste_kg:
        row.report_state === "available" ? formatNumber(row.metrics.waste_kg) : "-",
      solvent_volume_l:
        row.report_state === "available"
          ? formatNumber(row.metrics.solvent_volume_l)
          : "-",
      data_tb:
        row.report_state === "available"
          ? formatNullableNumber(row.metrics.data_tb)
          : "-",
      notes,
    } satisfies TableRow;
  });

export const AdminImpactEstimateReportsPage = ({
  labTypeSubscriptions,
}: ImpactEstimateReportsProps) => {
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = useState<ImpactReportLevel>("bronze");
  const [selectedLabType, setSelectedLabType] =
    useState<ImpactReportLabTypeFilter>("wet");
  const [selectedReportingYear, setSelectedReportingYear] = useState<number | null>(
    null,
  );

  const allowedLabTypes = useMemo(() => {
    const set = new Set<ImpactReportLabTypeFilter>();

    (labTypeSubscriptions ?? ["wet"]).forEach((value) => {
      if (value === "wet" || value === "dry") {
        set.add(value);
      }
    });

    if (set.size > 1) {
      set.add("all");
    }

    if (set.size === 0) {
      set.add("wet");
    }

    return Array.from(set);
  }, [labTypeSubscriptions]);

  useEffect(() => {
    setSelectedLabType((current) => {
      if (allowedLabTypes.includes(current)) {
        return current;
      }

      return allowedLabTypes[0] ?? "wet";
    });
  }, [allowedLabTypes]);

  const {
    data: yearsData,
    isLoading: isYearsLoading,
    isError: isYearsError,
    error: yearsError,
    refetch: refetchYears,
  } = useQuery<ImpactReportYearsResponse>({
    queryKey: ["admin-impact-report-years"],
    queryFn: () => ImpactReportService.getAdminImpactReportYears(),
  });

  useEffect(() => {
    if (!yearsData) {
      return;
    }

    setSelectedReportingYear((currentYear) => {
      if (
        currentYear !== null &&
        yearsData.years.includes(currentYear)
      ) {
        return currentYear;
      }

      if (yearsData.years.includes(yearsData.default_year)) {
        return yearsData.default_year;
      }

      return yearsData.years[0] ?? yearsData.default_year;
    });
  }, [yearsData]);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useQuery<OrganisationImpactReport>({
      queryKey: [
        "admin-impact-reports",
        selectedLevel,
        selectedLabType,
        selectedReportingYear,
      ],
      queryFn: () =>
        ImpactReportService.getAdminImpactReports({
          level: selectedLevel,
          lab_type: selectedLabType,
          reporting_year: selectedReportingYear ?? undefined,
        }),
      enabled:
        allowedLabTypes.includes(selectedLabType) &&
        typeof selectedReportingYear === "number",
    });

  const rows = data ? toLabRows(data) : [];
  const totalsRow: TableRow[] = data
    ? [
        {
          id: "totals",
          cost_aud: formatNumber(data.totals.cost_aud),
          energy_kwh: formatNumber(data.totals.energy_kwh),
          emissions_kgco2e: formatNumber(data.totals.emissions_kgco2e),
          waste_kg: formatNumber(data.totals.waste_kg),
          solvent_volume_l: formatNumber(data.totals.solvent_volume_l),
          data_tb: formatNullableNumber(data.totals.data_tb),
        },
      ]
    : [];

  const handleExportCsv = async () => {
    if (selectedReportingYear === null) {
      return;
    }

    await ImpactReportService.downloadAdminImpactReportCsv({
      level: selectedLevel,
      lab_type: selectedLabType,
      reporting_year: selectedReportingYear,
      filename: `impact-reports-${selectedLevel}-${selectedLabType}-${selectedReportingYear}.csv`,
    });
  };

  const yearOptions = yearsData?.years ?? [];
  const displayYear = data?.reporting_year ?? selectedReportingYear ?? yearsData?.default_year;
  const isLoadingState = isYearsLoading || isLoading || isFetching;
  const hasYearOptions = yearOptions.length > 0;

  return (
    <div className="l0-layout">
      <PageHeader
        pageTitle="Impact Estimate Reports"
        adminGuidePath="/admin-guide"
        backButton={
          <Button
            variant="neutral"
            small
            onClick={() => {
              navigate("..");
            }}
          >
            Back to dashboard
          </Button>
        }
      >
        <div>
          <p>Depending on your subscription, on this page you can view:</p>
          <ul>
            <li>
              Individual Lab Reports - Impact estimates for each registered wet
              lab and/or dry lab.
            </li>
            <li>
              Aggregate Organisational Report - Combined results across all labs
              registered under your organisation.
            </li>
            <li>
              Reports are generated from the baseline and savings assessments
              entered by your registered labs.
            </li>
            <li>All reports are estimates and intended as a guide only.</li>
          </ul>
        </div>
      </PageHeader>

      <div className="mt-8 flex flex-col gap-8">
        <Card variant="secondary">
          <div className="card-body">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h2>
                  Organisation Impact Reports
                  {typeof displayYear === "number" ? ` (${displayYear})` : ""}
                </h2>
                <p className="text-sm text-base-content/70 mt-1">
                  Filter impact reports by certification level, lab type and
                  reporting year to inspect report state and totals.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <label className="form-control w-full sm:w-40">
                  <div className="label py-1">
                    <span className="label-text text-xs">Level</span>
                  </div>
                  <select
                    className="select select-bordered select-sm"
                    value={selectedLevel}
                    onChange={(event) => {
                      setSelectedLevel(event.target.value as ImpactReportLevel);
                    }}
                  >
                    <option value="bronze">Bronze</option>
                    <option value="silver">Silver</option>
                  </select>
                </label>

                <label className="form-control w-full sm:w-40">
                  <div className="label py-1">
                    <span className="label-text text-xs">Lab Type</span>
                  </div>
                  <select
                    className="select select-bordered select-sm"
                    value={selectedLabType}
                    disabled={allowedLabTypes.length === 1}
                    onChange={(event) => {
                      setSelectedLabType(
                        event.target.value as ImpactReportLabTypeFilter,
                      );
                    }}
                  >
                    {allowedLabTypes.includes("wet") && (
                      <option value="wet">Wet</option>
                    )}
                    {allowedLabTypes.includes("dry") && (
                      <option value="dry">Dry</option>
                    )}
                    {allowedLabTypes.includes("all") && (
                      <option value="all">All</option>
                    )}
                  </select>
                </label>

                <label className="form-control w-full sm:w-40">
                  <div className="label py-1">
                    <span className="label-text text-xs">Year</span>
                  </div>
                  <select
                    className="select select-bordered select-sm"
                    value={selectedReportingYear ?? ""}
                    disabled={!hasYearOptions || yearOptions.length === 1}
                    onChange={(event) => {
                      setSelectedReportingYear(Number(event.target.value));
                    }}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {isLoadingState ? (
              <div className="py-8 text-center text-base-content/70">
                <span className="loading loading-spinner loading-sm" />
                <p className="text-sm mt-2">Loading impact reports...</p>
              </div>
            ) : null}

            {isYearsError ? (
              <div className="alert alert-error mt-4">
                <span>
                  {yearsError instanceof Error
                    ? yearsError.message
                    : "Failed to load impact report years"}
                </span>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => refetchYears()}
                >
                  Retry
                </button>
              </div>
            ) : null}

            {isError ? (
              <div className="alert alert-error mt-4">
                <span>
                  {error instanceof Error
                    ? error.message
                    : "Failed to load impact reports"}
                </span>
                <button className="btn btn-sm btn-outline" onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            ) : null}

            {!isLoadingState && !isYearsError && !isError && data && rows.length === 0 ? (
              <div className="py-8 text-center text-base-content/60 bg-base-100 rounded-lg border border-base-200 mt-4">
                <p className="font-medium mb-1">No report rows yet</p>
                <p className="text-sm">
                  Complete baseline and savings submissions to generate available
                  impact report rows.
                </p>
              </div>
            ) : null}

            {!isLoadingState && !isYearsError && !isError && data && rows.length > 0 ? (
              <>
                <div className="mt-4">
                  <Table
                    columns={withSolventVolume(labTableColumns, selectedLevel, "notes")}
                    data={rows}
                    hover={false}
                  />
                </div>

                <div className="mt-6">
                  <h3 className="text-base font-semibold mb-2">
                    Organisation Totals (Available Rows)
                  </h3>
                  <Table
                    columns={withSolventVolume(totalsColumns, selectedLevel, "data_tb")}
                    data={totalsRow}
                    hover={false}
                  />
                </div>

                <div className="card-actions mt-4 justify-end">
                  <button className="btn btn-secondary" onClick={handleExportCsv}>
                    Export CSV
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </Card>
      </div>
    </div>
  );
};
